import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";

import clsx from "clsx";
import _ from "lodash";
import React, { useCallback, useState } from "react";

import "react-datetime/css/react-datetime.css";
import "../../../theme/custom.css";
import EventEditorHeaderAction from "./EventEditorHeaderAction";

const shrink = { shrink: true };

const shallowEqual = (a, b) => {
  return _.isEqualWith(a, b, (x, y) => {
    if (!_.isObject(x) && !_.isObject(y)) {
      return x === y;
    }

    return undefined;
  });
};

export const MemoTextField = React.memo(TextField, (a, b) => {
  return shallowEqual(_.omit(a, "children"), _.omit(b, "children"));
});

export const useCustomEventStyles = makeStyles((theme) => ({
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  dangerButton: {
    color: colors.red[600],
    border: `1px solid ${alpha(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: alpha(
        colors.red[600],
        theme.palette.action.hoverOpacity
      ),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  createEventButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
  },
}));

export function CustomEventDisplay({
  editorState,
  setEventHidden,
  setEditorState,
  onSave,
  onClose,
}) {
  const [hasChanges, setHasChanges] = useState(false);
  const classes = useCustomEventStyles();

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setEditorState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setEditorState]
  );

  const setDescription = useCallback(
    function setName(e) {
      if (!hasChanges) {
        setHasChanges(true);
      }

      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange, hasChanges]
  );

  return (
    <Card>
      <CardHeader
        title="View Custom Event"
        action={
          <EventEditorHeaderAction
            editorState={editorState}
            onClose={onClose}
          />
        }
      />
      <Divider />
      <CardContent>
        <div style={{ paddingLeft: "15px" }}>
          <Typography variant="h5">{editorState.name}</Typography>
        </div>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>

        <Grid container style={{ marginTop: 16 }}>
          <Button
            className={clsx(classes.dangerButton)}
            onClick={() => setEventHidden(!editorState.is_hidden)}
          >
            {editorState.is_hidden ? "Restore Event" : "Hide Event"}
          </Button>
          {hasChanges && (
            <Box className={classes.buttonGroup}>
              <Button
                className={classes.dangerButton}
                onClick={onClose}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                style={{ marginLeft: "auto" }}
                className={classes.createEventButton}
                onClick={onSave}
                variant="outlined"
              >
                Save
              </Button>
            </Box>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
